import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';

export const PricingParams = () => ({
  spark: { companies: 1, campaigns: 1, affiliates: 300, members: 1, crypto: 0 },
  boost: { companies: 1, campaigns: 3, affiliates: 1000, members: 1, crypto: 1 },
  rocket: { companies: 3, campaigns: 10, affiliates: 3000, members: 3, crypto: 1 },
  daily: { companies: 3, campaigns: 10, affiliates: 3000, members: 3, crypto: 1 },
  galaxy: { companies: 10, campaigns: 30, affiliates: 10000, members: 20, crypto: 1 },
});

const InfoTooltip = ({ text }) => (
    <div
        className="
      pointer-events-none
      absolute left-1/2 -translate-x-1/2 bottom-full mb-2
      hidden group-hover:block
      w-64 px-3 py-2
      bg-gray-800 text-white text-sm rounded-lg shadow-xl z-30
    "
        role="tooltip"
    >
      {text}
    </div>
);

const buildPlanFeatures = (plans) => ({
  spark: [
    { id: 'affiliates', count: plans.spark.affiliates },
    { id: 'companies', count: plans.spark.companies },
    { id: 'campaigns', count: plans.spark.campaigns },
    { id: 'affiliateDashboard' },
    { id: 'support' },
    { id: 'analytics' },
    { id: 'paypal' },
  ],
  boost: [
    { id: 'affiliates', count: plans.boost.affiliates },
    { id: 'companies', count: plans.boost.companies },
    { id: 'campaigns', count: plans.boost.campaigns },
    { id: 'affiliateDashboard' },
    { id: 'support' },
    { id: 'analytics' },
    { id: 'personalManagerOnboarding' },
    { id: 'multiTierRewardSystems' },
    { id: 'emailNotifications' },
    { id: 'paypal' },
    { id: 'crypto' },
    { id: 'invitePage' },
  ],
  rocket: [
    { id: 'affiliates', count: plans.rocket.affiliates },
    { id: 'companies', count: plans.rocket.companies },
    { id: 'campaigns', count: plans.rocket.campaigns },
    { id: 'affiliateDashboard' },
    { id: 'support' },
    { id: 'analytics' },
    { id: 'personalManagerOnboarding' },
    { id: 'multiTierRewardSystems' },
    { id: 'emailNotifications' },
    { id: 'paypal' },
    { id: 'crypto' },
    { id: 'invitePage' },
    { id: 'inviteTeam', count: plans.rocket.members },
  ],
  daily: [
    { id: 'affiliates', count: plans.daily.affiliates },
    { id: 'companies', count: plans.daily.companies },
    { id: 'campaigns', count: plans.daily.campaigns },
    { id: 'affiliateDashboard' },
    { id: 'support' },
    { id: 'analytics' },
    { id: 'personalManagerOnboarding' },
    { id: 'multiTierRewardSystems' },
    { id: 'emailNotifications' },
    { id: 'paypal' },
    { id: 'crypto' },
    { id: 'invitePage' },
    { id: 'inviteTeam', count: plans.daily.members },
  ],
  galaxy: [
    { id: 'affiliates', count: plans.galaxy.affiliates },
    { id: 'companies', count: plans.galaxy.companies },
    { id: 'campaigns', count: plans.galaxy.campaigns },
    { id: 'affiliateDashboard' },
    { id: 'supportPriority' },
    { id: 'analytics' },
    { id: 'personalManagerOnboarding' },
    { id: 'multiTierRewardSystems' },
    { id: 'emailNotifications' },
    { id: 'paypal' },
    { id: 'crypto' },
    { id: 'invitePage' },
    { id: 'inviteTeam', count: plans.galaxy.members },
    { id: 'customFeatures' },
  ],
});

export const planHasFeature = (planName, featureId) => {
  if (!planName || !featureId) return false;

  const plans = PricingParams();
  const allFeaturesByPlan = buildPlanFeatures(plans);
  const normalizedPlanName = String(planName).toLowerCase();
  const features = allFeaturesByPlan[normalizedPlanName] || [];

  return features.some((feature) => feature.id === featureId);
};

export const PricingFeatures = ({ productName, isPopular }) => {
  const t = useTranslations('pricing');
  if (!productName) return null;

  const plans = PricingParams();
  const key = productName.toLowerCase();
  const allFeaturesByPlan = buildPlanFeatures(plans);
  const features = (allFeaturesByPlan[key] || []);

  const inheritedFrom = {
          boost: 'spark',
          rocket: 'boost',
          galaxy: 'rocket',
  };

  const baseKey = inheritedFrom[key];

  const baseFeatures = baseKey ? (allFeaturesByPlan[baseKey] || []) : [];

        const getExtras = (current, base) => {
        const baseMap = new Map(base.map((f) => [f.id, f]));
        return current.filter((f) => {
            const b = baseMap.get(f.id);
            if (!b) return true; // new feature not in base
            const fHasCount = typeof f.count === 'number';
            const bHasCount = typeof b.count === 'number';
            if (fHasCount || bHasCount) return f.count !== b.count; // limits changed
            return false; // same feature already included
          });
      };

        const shouldShowInheritanceFormat = Boolean(baseKey) && key !== 'daily';
    const displayedFeatures = shouldShowInheritanceFormat ? getExtras(features, baseFeatures) : features;


  const getLabel = (f) => {
    const path = `features.labels.${f.id}`;
    if (typeof f.count === 'number') return t(path, { count: f.count });
    return t(path);
  };

  const getTooltip = (f) => {
    const path = `features.tooltips.${f.id}`;
    try {
      return t(path);
    } catch {
      return t('features.fallbackTooltip');
    }
  };

  return (
      <ul className="space-y-3">
        {shouldShowInheritanceFormat && (
           <li className={`text-sm ${isPopular ? 'text-indigo-100' : 'text-gray-700'}`}>
             {t('features.labels.everythingInPlus', {
                  plan: baseKey.charAt(0).toUpperCase() + baseKey.slice(1),
             })}
           </li>
        )}

      {displayedFeatures.map((f, idx) => {
          const label = getLabel(f);
          const explanation = getTooltip(f);

          return (
              <motion.li
                  key={`${f.id}-${idx}`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="flex items-center gap-3"
              >
                <CheckCircleIcon className={`w-5 h-5 ${isPopular ? 'text-white' : 'text-green-500'}`} />
                <div
                    className={`
                relative group
                flex justify-between items-start gap-2 w-full
                text-sm ${isPopular ? 'text-indigo-100' : 'text-gray-700'}
              `}
                >
                  <span className="flex-1 break-words">{label}</span>

                  <svg
                      className="w-4 h-4 scale-[0.8] flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                  >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>

                  <InfoTooltip text={explanation} />
                </div>
              </motion.li>
          );
        })}
      </ul>
  );
};

export default PricingFeatures;
